'use client';

import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, X } from 'lucide-react';
import { useIsTVDevice } from '../hooks/useTVNavigation';

interface LoadingOverlayProps {
  isLoading: boolean;
  message?: string;
  onCancel?: () => void;
  showTimeout?: boolean;
}

export default function LoadingOverlay({ 
  isLoading, 
  onCancel,
  showTimeout = false
}: LoadingOverlayProps) {
  const isTV = useIsTVDevice();

  // Auto-focus cancel button on TV
  useEffect(() => {
    if (isLoading && isTV && onCancel) {
      const cancelButton = document.getElementById('loading-cancel-button');
      setTimeout(() => {
        cancelButton?.focus();
      }, 500);
    }
  }, [isLoading, isTV, onCancel]);

  // Handle Back button on TV to cancel
  useEffect(() => {
    if (!isLoading || !onCancel) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Back' || e.key === 'XF86Back') {
        e.preventDefault();
        e.stopPropagation();
        onCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isLoading, onCancel]);

  if (!isLoading) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md"
        role="dialog"
        aria-modal="true"
        aria-labelledby="loading-title"
      >
        {/* Loading Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="relative"
        >
          {/* Spinner with glow */}
          <div className="relative">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            >
              <Loader2 className={`text-red-600 ${isTV ? 'w-24 h-24' : 'w-20 h-20'}`} />
            </motion.div>
            
            {/* Glow effect */}
            <div className={`absolute inset-0 ${isTV ? 'blur-3xl' : 'blur-2xl'} opacity-60`}>
              <Loader2 className={`text-red-600 ${isTV ? 'w-24 h-24' : 'w-20 h-20'}`} />
            </div>
          </div>

          {/* Minimal timeout indicator */}
          {showTimeout && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`absolute ${isTV ? '-bottom-16' : '-bottom-12'} left-1/2 -translate-x-1/2 whitespace-nowrap`}
            >
              <div className={`flex items-center gap-2 text-yellow-400 ${isTV ? 'text-xl' : 'text-base'}`}>
                <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
                <span>Loading...</span>
              </div>
            </motion.div>
          )}

          {/* Cancel Button - Small, unobtrusive */}
          {onCancel && (
            <button
              id="loading-cancel-button"
              onClick={onCancel}
              className={`absolute ${isTV ? '-top-20 -right-20' : '-top-16 -right-16'} p-3 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-sm transition-all hover:scale-110 ${isTV ? 'focus:ring-4 focus:ring-red-600 focus:scale-110' : ''}`}
              aria-label="Cancel loading"
              tabIndex={isTV ? 0 : undefined}
            >
              <X className={`text-gray-400 hover:text-white transition-colors ${isTV ? 'w-8 h-8' : 'w-6 h-6'}`} />
            </button>
          )}
        </motion.div>

        {/* Minimal TV hint - only on TV */}
        {isTV && onCancel && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="absolute bottom-8"
          >
            <p className="text-gray-500 text-sm">
              Press <span className="text-gray-400">Back</span> to cancel
            </p>
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}

